import React from 'react';
import { ChevronDown, ChevronRight, MoreHorizontal } from 'lucide-react';
import { resolveIcon } from './icons';
import Tooltip from '../../components/ui/Tooltip';

const SIDEBAR_TREE_ROW_IDLE =
  'border border-transparent hover:bg-gray-50 hover:border-gray-200 dark:hover:bg-zinc-800 dark:hover:border-zinc-700';

const BADGE_TONE_CLASS = {
  default: 'bg-gray-100 text-gray-600 dark:bg-zinc-800 dark:text-zinc-300',
  success: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  error: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
};

// Icon-only badges (e.g. a DB connection-error alert triangle) render bare —
// no pill background, just the tone-colored glyph.
const BADGE_ICON_TONE_CLASS = {
  default: 'text-gray-500 dark:text-zinc-400',
  success: 'text-green-600 dark:text-green-400',
  error: 'text-red-600 dark:text-red-400',
  warning: 'text-amber-600 dark:text-amber-400',
};

// node.arrowTone colours the plain expand chevron. Only the chevron — not a
// badge in its slot. (The DB Manager's connected root used it until 06.10.2026;
// it shows a status dot now — StatusDot below.)
const ARROW_TONE_CLASS = {
  success: 'sidebar-collection-chevron-btn--success',
};

/**
 * node.status: a connection's state as a dot in the icon slot, the FTP
 * sidebar's dot (owner, 06.10.2026) — { state: 'disconnected' | 'connecting'
 * | 'connected' | 'error', tooltip, action }. With an `action` it is a toggle:
 * a click runs it (connect, disconnect, retry) without expanding the row.
 */
function StatusDot({ node, onAction }) {
  const { state = 'disconnected', tooltip, action } = node.status;
  const dot = <span className={`tree-status-dot tree-status-dot--${state}`} aria-hidden />;
  return (
    <Tooltip content={tooltip} side="bottom-start">
      {action ? (
        <button
          type="button"
          className="tree-status-btn"
          aria-label={tooltip || action}
          onClick={(e) => {
            e.stopPropagation();
            onAction(node, action);
          }}
        >
          {dot}
        </button>
      ) : (
        <span className="tree-status-btn" role="img" aria-label={tooltip || state}>
          {dot}
        </span>
      )}
    </Tooltip>
  );
}

// A node's status pill. Renders an optional lucide icon (badge.icon) alongside
// any text, and — when badge.tooltip is set — reveals the full detail (e.g. a
// DB connection error) in a portal tooltip on hover instead of a plain,
// truncated label.
//
// placement 'left' seats an icon-only badge (the DB connection-error alert, or
// the connecting spinner) in the leaf's arrow/chevron slot instead of trailing
// the label. marginLeft nudges the glyph rightward to sit under the branch rows'
// expand caret. badge.spin animates the glyph (used for the connecting loader).
function NodeBadge({ badge, placement = 'right' }) {
  const BadgeIcon = badge.icon ? resolveIcon(badge.icon) : null;
  const iconOnly = BadgeIcon && !badge.text;
  const left = placement === 'left';
  const pill = (
    <span
      role={iconOnly ? 'img' : undefined}
      aria-label={iconOnly ? badge.tooltip || badge.icon : undefined}
      style={left ? { marginLeft: 7, marginRight: 9 } : { marginLeft: 4, marginRight: 6 }}
      className={
        iconOnly
          ? `shrink-0 inline-flex items-center ${BADGE_ICON_TONE_CLASS[badge.tone] || BADGE_ICON_TONE_CLASS.default}`
          : `shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium inline-flex items-center gap-1 ${
              BADGE_TONE_CLASS[badge.tone] || BADGE_TONE_CLASS.default
            }`
      }
    >
      {BadgeIcon && <BadgeIcon size={14} className={badge.spin ? 'animate-spin' : undefined} aria-hidden />}
      {badge.text}
    </span>
  );
  if (!badge.tooltip) return pill;
  return (
    <Tooltip
      side="bottom-start"
      content={
        // Inline width, not `max-w-[280px]`: index.css declares no
        // arbitrary-value utilities, so that class has never wrapped anything.
        <span style={{ display: 'block', maxWidth: 280 }} className="whitespace-normal break-words">
          {badge.tooltip}
        </span>
      }
    >
      {pill}
    </Tooltip>
  );
}

function nodePropsEqual(prev, next) {
  return (
    prev.node === next.node &&
    prev.depth === next.depth &&
    prev.expandedIds === next.expandedIds &&
    prev.searchActive === next.searchActive &&
    prev.onToggle === next.onToggle &&
    prev.onNodeClick === next.onNodeClick &&
    prev.onAction === next.onAction &&
    prev.onActionsMenu === next.onActionsMenu
  );
}

const ExtensionTreeNode = React.memo(function ExtensionTreeNode({
  node,
  depth,
  expandedIds,
  searchActive,
  onToggle,
  onNodeClick,
  onAction,
  onActionsMenu,
}) {
  const Icon = resolveIcon(node.icon);
  const isBranch = node.kind === 'branch';
  const isOpen = isBranch && (searchActive || expandedIds.has(node.id));
  const children = node.children === 'lazy' ? (isOpen ? node.resolvedChildren || [] : []) : node.children || [];
  const hasActions = node.actions?.length > 0;
  // An icon-only badge (the connecting spinner, or a connection-error alert)
  // seats itself in the arrow/chevron slot on the left, taking over the caret
  // slot — so a connection row shows exactly one glyph there across its states:
  // the expand caret when idle, the spinner while connecting, the error alert
  // when a connect failed. A badge carrying an `action` is clickable (the alert
  // retries the connect, which swaps it back to the spinner); a badge without
  // one (the spinner) is inert.
  const badgeInArrowSlot = !!node.badge?.icon && !node.badge?.text;

  return (
    <div>
      <div
        style={{ paddingLeft: `${4 + depth * 14}px` }}
        className={`flex items-center px-2 py-1 rounded-md group cursor-pointer transition-colors duration-150 ${SIDEBAR_TREE_ROW_IDLE}`}
        onClick={() => onNodeClick(node)}
        // node.doubleClickAction: the action a double-click runs (a database
        // row opens its overview, as in DBeaver/DataGrip). Only such rows
        // react; for the rest a double-click stays two clicks.
        onDoubleClick={
          node.doubleClickAction
            ? (e) => {
                e.stopPropagation();
                onAction(node, node.doubleClickAction);
              }
            : undefined
        }
        onContextMenu={
          hasActions
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
                onActionsMenu(e, node);
              }
            : undefined
        }
      >
        {badgeInArrowSlot ? (
          node.badge.action ? (
            // Clickable status glyph (the error alert): retries the connection,
            // which re-publishes the row with the spinner badge in this slot.
            <button
              type="button"
              className="shrink-0 flex items-center bg-transparent p-0 rounded cursor-pointer"
              // Nudge the alert a hair left of the spinner/caret so the wider
              // triangle glyph reads as sitting in the same slot, not past it.
              style={{ marginLeft: -6 }}
              onClick={(e) => {
                e.stopPropagation();
                onAction(node, node.badge.action);
              }}
              aria-label={node.badge.tooltip || 'Retry'}
            >
              <NodeBadge badge={node.badge} placement="left" />
            </button>
          ) : (
            <NodeBadge badge={node.badge} placement="left" />
          )
        ) : isBranch ? (
          <button
            type="button"
            className={`sidebar-collection-chevron-btn mr-1 shrink-0 flex items-center p-0.5 rounded${
              ARROW_TONE_CLASS[node.arrowTone] ? ' ' + ARROW_TONE_CLASS[node.arrowTone] : ''
            }`}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(node);
            }}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
          >
            {isOpen ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
          </button>
        ) : (
          // The caret slot a LEAF reserves so it lines up with the branches
          // around it: the chevron button is a 14px glyph plus `mr-1`, so 18.
          //
          // Inline, not `w-[18px]`. index.css is a hand-rolled utility subset
          // with NO arbitrary-value classes in it at all, so that one has always
          // resolved to nothing and this spacer has always been zero wide — the
          // DB Manager's "Vault locked" row sat flush left while its connection
          // rows were indented, and the FTP sidebar (built to match the
          // indented ones) looked 18px out beside it (owner, 23.09.2026).
          <span style={{ width: 18 }} className="shrink-0" aria-hidden />
        )}
        {/* `mr-2`, not `mr-1`: 4px left the glyph crowding its own label, and the
            FTP sidebar's rows are built to this same measurement — the two
            panels sit one tab apart (owner, 23.09.2026). Keep them in step;
            `ftpDbSidebarParity.test.js` fails if they drift. */}
        {node.status ? (
          <StatusDot node={node} onAction={onAction} />
        ) : (
          <Icon size={12} className="text-gray-500 dark:text-zinc-400 shrink-0 mr-2" aria-hidden />
        )}
        {/* The full name after a second's hover (owner, 06.10.2026): a narrow
            sidebar cuts long table names, and widening it just to read one is
            a chore. Delayed so moving the mouse down the tree does not flicker. */}
        <Tooltip content={node.label} side="right" delay={1000}>
          <span className={`${node.tag ? 'min-w-0' : 'flex-1 min-w-0'} text-xs font-medium text-gray-800 dark:text-zinc-200 truncate`}>
            {node.label}
          </span>
        </Tooltip>
        {node.tag && (
          <span className="tree-node-tag-wrap">
            <span className="tree-node-tag">{node.tag}</span>
          </span>
        )}
        {node.badge && !badgeInArrowSlot && <NodeBadge badge={node.badge} />}
        {hasActions && (
          // Node actions live behind the same "…" dropdown Collections rows
          // use (the actions themselves render in ExtensionTreeSection's
          // context menu) — no more per-action inline hover icons.
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onActionsMenu(e, node);
            }}
            className="opacity-0 group-hover:opacity-100 shrink-0 p-1 bg-transparent hover:bg-gray-200 dark:hover:bg-zinc-700 rounded transition-colors duration-150 cursor-pointer ml-1"
          >
            <MoreHorizontal size={12} className="text-gray-500 dark:text-zinc-400" />
          </button>
        )}
      </div>
      {isBranch &&
        isOpen &&
        children.map((child) => (
          <ExtensionTreeNode
            key={child.id}
            node={child}
            depth={depth + 1}
            expandedIds={expandedIds}
            searchActive={searchActive}
            onToggle={onToggle}
            onNodeClick={onNodeClick}
            onAction={onAction}
            onActionsMenu={onActionsMenu}
          />
        ))}
    </div>
  );
},
nodePropsEqual);

export default ExtensionTreeNode;
